import { useRef, useState } from 'react'
import { NotebookPen, Sparkle } from 'lucide-react'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import {
  displayNameFor,
  undecidedSummary,
} from '@/features/deal-review/deal-lead-views'
import { useIsClamped } from '@/hooks/use-is-clamped'
import { useCriterionLabels } from '@/hooks/use-criterion-labels'
import { useSaveDealLeadNote } from '@/hooks/use-deal-leads'
import { showError } from '@/lib/toast'
import { Textarea } from '@/components/ui/textarea'
import type { DealLeadListItem } from '@/types/api'
import {
  OwnClick,
  RevealWhenClamped,
} from '@/components/custom/deal-review/inbox-cells'

// `key` remounts on a new note so the clamp is measured again.
function NoteText({
  text,
  className,
  measure,
}: {
  text: string
  className?: string
  measure: (node: HTMLElement | null) => void
}) {
  return (
    <span
      key={text}
      ref={measure}
      className={cn(
        'line-clamp-2 whitespace-normal break-words text-xs leading-5',
        className
      )}
    >
      {text}
    </span>
  )
}

export function NotesCell({
  lead,
  readOnly,
}: {
  lead: DealLeadListItem
  readOnly: boolean
}) {
  const save = useSaveDealLeadNote(lead.id)
  const labels = useCriterionLabels()
  const saved = lead.dispositionCommentary ?? ''
  // Seeded but saved only once edited or kept: a note is never the model's text under a person's name.
  const needsInformation =
    lead.disposition === 'UNREVIEWED' &&
    lead.recommendedDisposition === 'NEEDS_INFORMATION'
  const suggestion =
    (needsInformation
      ? undecidedSummary(lead.unknownCriterionIds, labels)
      : null) ??
    lead.recommendationReason ??
    ''

  const [measure, clamped] = useIsClamped()

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(saved)
  const [seeded, setSeeded] = useState(false)
  const [touched, setTouched] = useState(false)
  // Enter blurs to save, and state set on the way out hasn't landed when blur reads it.
  const keeping = useRef(false)

  function commit() {
    setEditing(false)
    const kept = keeping.current
    keeping.current = false
    if (seeded && !touched && !kept) {
      setDraft(saved)
      return
    }
    if (draft.trim() === saved.trim()) return

    save.mutate(
      { version: lead.version, commentary: draft.trim() === '' ? null : draft },
      {
        onError: (error) => {
          setDraft(saved)
          showError(error, 'That note could not be saved')
        },
      }
    )
  }

  if (readOnly || !lead.capabilities.canCorrect) {
    return saved === '' ? (
      <Typography as="span" variant="body-sm" className="text-primary/40">
        —
      </Typography>
    ) : (
      <RevealWhenClamped clamped={clamped} text={saved}>
        <span
          tabIndex={clamped ? 0 : undefined}
          className="block rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <NoteText
            text={saved}
            className="text-primary/75"
            measure={measure}
          />
        </span>
      </RevealWhenClamped>
    )
  }

  if (editing) {
    return (
      <OwnClick>
        <Textarea
          autoFocus
          rows={2}
          value={draft}
          placeholder="Add a note"
          disabled={save.isPending}
          aria-label={`Note for ${displayNameFor(lead) ?? 'this lead'}`}
          className="min-h-14 resize-none py-1 text-xs leading-5"
          onChange={(event) => {
            setTouched(true)
            setDraft(event.target.value)
          }}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setDraft(saved)
              setSeeded(false)
              setEditing(false)
            }
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              keeping.current = true
              event.currentTarget.blur()
            }
          }}
        />
        {seeded && !touched && (
          <Typography
            as="span"
            variant="body-sm"
            className="mt-1 flex items-center gap-1 text-xs text-primary/70"
          >
            <Sparkle aria-hidden="true" className="size-3 shrink-0" />
            From screening
          </Typography>
        )}
      </OwnClick>
    )
  }

  return (
    <OwnClick>
      <RevealWhenClamped
        clamped={clamped}
        text={saved !== '' ? saved : suggestion}
      >
        <button
          type="button"
          onClick={() => {
            keeping.current = false
            const seed = saved === '' && suggestion !== ''
            setDraft(seed ? suggestion : saved)
            setSeeded(seed)
            setTouched(false)
            setEditing(true)
          }}
          aria-label={`Note for ${displayNameFor(lead) ?? 'this lead'}`}
          className="w-full rounded-sm px-1 py-0.5 text-left hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {saved === '' && suggestion !== '' && !save.isPending ? (
            <Typography
              as="span"
              variant="body-sm"
              className="flex gap-1.5 text-primary/70"
            >
              <Sparkle
                aria-hidden="true"
                className="mt-1 size-3 shrink-0 text-primary/40"
              />
              <NoteText
                text={suggestion}
                className="italic"
                measure={measure}
              />
            </Typography>
          ) : saved === '' ? (
            <Typography
              as="span"
              variant="body-sm"
              className="inline-flex items-center gap-1.5 text-primary/40"
            >
              <NotebookPen aria-hidden="true" className="size-3.5 shrink-0" />
              {save.isPending ? 'Saving…' : 'Add a note'}
            </Typography>
          ) : (
            <NoteText
              text={saved}
              className="text-primary/75"
              measure={measure}
            />
          )}
        </button>
      </RevealWhenClamped>
    </OwnClick>
  )
}
